import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Sun, Navigation, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { PageBanner, Txt, PILLARS } from "@/components/site/Brand";
import { InquiryForm } from "@/components/site/InquiryForm";
import { ContactList } from "@/components/site/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const FORMS = [
  { key: "general", label: "General contact", title: "General contact", catLabel: "Topic" },
  { key: "consultancy", label: "Request a consultation", title: "Consultancy request", catLabel: "Service area" },
  { key: "partnership", label: "Partnership inquiry", title: "Partnership inquiry", catLabel: "Partnership type" },
];

export function Contact() {
  const [params, setParams] = useSearchParams();
  const { services, settings } = useSite();
  const [tax, setTax] = useState(null);
  useEffect(() => { document.title = "Contact – IRID"; api.get("/taxonomies").then((r) => setTax(r.data)); }, []);
  const tab = FORMS.some((f) => f.key === params.get("form")) ? params.get("form") : "general";
  const svc = services.find((s) => s.slug === params.get("service"));
  return (
    <>
      <PageBanner title="Contact IRID" eyebrow="Get in touch" intro="Send a general question, request a consultation, or explore a partnership with IRID." crumbs={[{ to: "/", label: "Home" }, { label: "Contact" }]} testid="contact-banner" />
      <section className="bg-irid-mist py-16 md:py-20">
        <div className="container-irid grid gap-10 lg:grid-cols-12">
          <aside className="lg:col-span-4">
            <div className="card-irid card-top-growth p-7 pt-8" data-testid="contact-details-card">
              <h2 className="text-xl">Office</h2>
              <div className="mt-5"><ContactList /></div>
              <div className="mt-7 flex flex-col gap-3">
                {settings?.map_url && (
                  <a href={settings.map_url} target="_blank" rel="noopener noreferrer" className="btn-secondary w-full" data-testid="contact-directions-link">
                    <Navigation aria-hidden="true" className="h-4 w-4" />Get directions<span className="sr-only"> (opens Google Maps in a new tab)</span>
                  </a>
                )}
                {settings?.whatsapp && (
                  <a href={settings.whatsapp.href} target="_blank" rel="noopener noreferrer" className="btn-primary w-full" data-testid="contact-whatsapp-link">
                    <MessageCircle aria-hidden="true" className="h-4 w-4" />Message us on WhatsApp<span className="sr-only"> at {settings.whatsapp.display} (opens in a new tab)</span>
                  </a>
                )}
              </div>
              {settings?.whatsapp && <p className="mt-3 text-sm text-irid-slate">WhatsApp: {settings.whatsapp.display}. Please don’t share sensitive personal information over WhatsApp.</p>}
            </div>
          </aside>
          <div className="card-irid p-6 md:p-10 lg:col-span-8">
            <Tabs value={tab} onValueChange={(v) => setParams({ form: v })}>
              <TabsList className="mb-8 flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
                {FORMS.map((f) => (
                  <TabsTrigger key={f.key} value={f.key} data-testid={`contact-tab-${f.key}`}
                    className="min-h-[44px] rounded-lg border border-irid-cloud px-4 font-semibold text-irid-navy data-[state=active]:border-irid-navy data-[state=active]:bg-irid-navy data-[state=active]:text-white data-[state=active]:shadow-none">
                    {f.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {tax && FORMS.map((f) => (
                <TabsContent key={f.key} value={f.key}>
                  <h2 className="text-2xl md:text-3xl">{f.title}</h2>
                  <div className="growth-bar mb-8 mt-3" aria-hidden="true" />
                  <InquiryForm formType={f.key} categories={tax.inquiry_categories[f.key]} categoryLabel={f.catLabel}
                    defaultCategory={f.key === "consultancy" && svc ? svc.title : ""} />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </section>
    </>
  );
}

export function Privacy() {
  const { settings } = useSite();
  useEffect(() => { document.title = "Privacy notice – IRID"; }, []);
  return (
    <>
      <PageBanner title="Privacy notice" eyebrow="Legal" crumbs={[{ to: "/", label: "Home" }, { label: "Privacy" }]} testid="privacy-banner" />
      <section className="bg-white py-16">
        <div className="container-irid max-w-3xl text-lg" data-testid="privacy-notice-text"><Txt as="p">{settings?.privacy_notice}</Txt></div>
      </section>
    </>
  );
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [r, setR] = useState(null);
  useEffect(() => { document.title = "Search – IRID"; const v = params.get("q"); if (v) api.get("/search", { params: { q: v } }).then((x) => setR(x.data)); }, [params]);
  const total = r ? r.services.length + r.publications.length + r.news.length : 0;
  const group = (title, items, to, label) => items.length > 0 && (
    <div className="mb-10"><h2 className="text-2xl">{title}</h2><ul className="mt-4 space-y-3">
      {items.map((i) => <li key={i.slug}><Link to={`${to}/${i.slug}`} className="irid-link text-lg"><Txt>{i.title}</Txt></Link><span className="ml-2 text-sm text-irid-slate">{label}</span></li>)}
    </ul></div>
  );
  return (
    <>
      <PageBanner title="Search" crumbs={[{ to: "/", label: "Home" }, { label: "Search" }]} testid="search-banner" />
      <section className="bg-white py-16">
        <div className="container-irid max-w-3xl">
          <SearchBox q={q} setQ={setQ} onSubmit={() => setParams({ q })} testid="site-search" />
          {r && <p className="my-8 text-irid-slate" role="status" data-testid="site-search-count">{total} result{total === 1 ? "" : "s"} for “{params.get("q")}”</p>}
          {r && group("Services", r.services, "/services", "Service")}
          {r && group("Publications", r.publications, "/publications", "Publication")}
          {r && group("News", r.news, "/news", "Article")}
        </div>
      </section>
    </>
  );
}

function SearchBox({ q, setQ, onSubmit, testid }) {
  return (
    <form role="search" onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="flex gap-3">
      <label htmlFor={`${testid}-input`} className="sr-only">Search the IRID website</label>
      <input id={`${testid}-input`} type="search" className="field-input" placeholder="Search the IRID website" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-input`} />
      <button type="submit" className="btn-primary" data-testid={`${testid}-submit`}><Search aria-hidden="true" className="h-4 w-4" />Search</button>
    </form>
  );
}

export function NotFoundView() {
  const [q, setQ] = useState("");
  const { services } = useSite();
  useEffect(() => { document.title = "Page not found – IRID"; }, []);
  return (
    <section className="bg-white py-20 md:py-28" data-testid="not-found-page">
      <div className="container-irid grid items-center gap-14 lg:grid-cols-2">
        <div>
          <span className="eyebrow">Error 404</span>
          <h1 className="mt-4">We couldn’t find that page</h1>
          <p className="mt-5 text-lg text-irid-slate">The page may have moved or is no longer available. Try searching, or use one of the links below.</p>
          <div className="mt-8"><SearchBox q={q} setQ={setQ} onSubmit={() => { window.location.href = `/search?q=${encodeURIComponent(q)}`; }} testid="not-found-search" /></div>
          <ul className="mt-10 flex flex-wrap gap-3">
            {[["/", "Home"], ["/services", "Services"], ["/publications", "Research library"], ["/contact", "Contact IRID"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="btn-secondary" data-testid={`not-found-link-${l.toLowerCase().replace(/\s+/g, "-")}`}>{l}</Link></li>
            ))}
          </ul>
        </div>
        <div className="relative mx-auto flex h-72 w-72 items-center justify-center" aria-hidden="true">
          <div className="absolute inset-0 rounded-full bg-irid-mist" />
          <div className="absolute bottom-16 h-24 w-48 rounded-t-full bg-[image:var(--irid-sunrise)] opacity-90" />
          <Sun className="relative -mt-10 h-16 w-16 text-irid-orange-deep" />
          <div className="absolute bottom-14 h-3 w-60 rounded bg-[image:var(--irid-growth)]" />
          <div className="absolute bottom-6 flex gap-2">{services.map((s) => <span key={s.slug} className={`h-2 w-8 rounded ${PILLARS[s.pillar].bar}`} />)}</div>
        </div>
      </div>
    </section>
  );
}
